import { NotificationType } from '@api/READ_ONLY/notifications_api/Api';
import type { TFunction } from 'i18next';
import { NotificationTypeValue } from '../types/notifications';

// Every type here MUST have a card in NotificationSelectorByType. A type in this
// list that the selector answers with null survives the filter, takes a slot in a
// day group and renders nothing - which is what ShiftDeleted did until it was
// removed below, and what the two shift-conflict types did until they got a card.
// The selector is the source of truth; this list follows it.
export const SUPPORTED_NOTIFICATION_TYPES: NotificationTypeValue[] = [
  NotificationType.VisitUpdated,
  NotificationType.VisitRejected,
  NotificationType.VisitCompleted,
  NotificationType.DataExportCompleted,
  NotificationType.DataExportFailed,
  NotificationType.VisitAssignmentRemoved,
  NotificationType.VisitAssignmentAdded,
  NotificationType.VisitAssignmentBatchAdded,
  NotificationType.VisitScheduleUpdated,
  NotificationType.VisitRescheduled,
  NotificationType.VisitCheckInReminder,
  NotificationType.VisitCheckedIn,
  NotificationType.AttachmentAdded,
  NotificationType.AttachmentRemoved,
  NotificationType.VisitDeleteBatchCompleted,
  NotificationType.VisitBillingSyncBatchCompleted,
  NotificationType.VisitCompleteBatchCompleted,
  NotificationType.CaregiverCashDepositRecorded,
  NotificationType.NoteAdded,
  NotificationType.DoctorInstructionSent,
  NotificationType.DoctorInstructionDecided,
  NotificationType.DeleteShiftConflict,
  NotificationType.UpdateShiftConflict,
];

export const getNotificationFilterOptions = (
  t: TFunction
): Array<{ value: NotificationTypeValue | null; label: string }> => [
  { value: null, label: t('notifications-type-all') },
  { value: NotificationType.VisitUpdated, label: t('notifications-type-visit-updated') },
  { value: NotificationType.VisitCompleted, label: t('notifications-type-visit-completed') },
  { value: NotificationType.VisitRejected, label: t('notifications-type-visit-rejected') },
  { value: NotificationType.VisitAssignmentAdded, label: t('notifications-type-assignment-added') },
  { value: NotificationType.VisitAssignmentBatchAdded, label: t('notifications-type-assignment-batch-added') },
  { value: NotificationType.VisitScheduleUpdated, label: t('notifications-type-schedule-updated') },
  { value: NotificationType.VisitAssignmentRemoved, label: t('notifications-type-assignment-removed') },
  { value: NotificationType.VisitRescheduled, label: t('notifications-type-visit-rescheduled') },
  { value: NotificationType.VisitCheckInReminder, label: t('notifications-type-checkin-reminder') },
  { value: NotificationType.VisitCheckedIn, label: t('notifications-type-visit-checked-in') },
  { value: NotificationType.AttachmentAdded, label: t('notifications-type-attachment-added') },
  { value: NotificationType.AttachmentRemoved, label: t('notifications-type-attachment-removed') },
  { value: NotificationType.DataExportCompleted, label: t('notifications-type-export-completed') },
  { value: NotificationType.VisitDeleteBatchCompleted, label: t('notifications-type-visit-delete-batch') },
  { value: NotificationType.VisitBillingSyncBatchCompleted, label: t('notifications-type-update-billing') },
  { value: NotificationType.VisitCompleteBatchCompleted, label: t('notifications-type-visit-complete-batch') },
  { value: NotificationType.CaregiverCashDepositRecorded, label: t('notifications-type-cash-deposit-recorded') },
  { value: NotificationType.NoteAdded, label: t('notifications-type-note-added') },
  { value: NotificationType.DoctorInstructionSent, label: t('notifications-type-doctor-instruction-sent') },
  { value: NotificationType.DoctorInstructionDecided, label: t('notifications-type-doctor-instruction-decided') },
];

export const isDataExportNotificationType = (type?: string | null) =>
  type === NotificationType.DataExportCompleted || type === NotificationType.DataExportFailed;

export const isBatchOperationNotificationType = (type?: NotificationTypeValue | null) =>
  type === NotificationType.VisitDeleteBatchCompleted ||
  type === NotificationType.VisitBillingSyncBatchCompleted ||
  type === NotificationType.VisitCompleteBatchCompleted;
